import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { ApiService, InformationRequestStatus } from 'api';
import { useInformation409A } from 'common/hooks';
import { DocumentsContext } from 'context/DocumentsContext';
import InformationDashboard409AContext from 'context/InformationDashboard409AContext/InformationDashboard409AContext';
import { VALUATION_TYPE_409A } from 'dashboard409a/common/constants';
import { getCompanyRequestedInformation } from 'dashboard409a/services/dashboard';
import { hideLoadingProgressBar, showLoadingProgressBar } from 'dashboard409a/states/features/global';
import { Logger } from 'utilities/logger';
import { UseInformationRequestStatusesProps } from './types';
import { CompanyData } from '../../../dashboard409a/pages/Information/types';

const useInformationRequestStatuses = (props: UseInformationRequestStatusesProps) => {
  const { currentCompany, currentMeasurementDateId, currentCompanyMeasurementDateId, is409a, reFetch } = props;
  const [informationRequestStatuses, setInformationRequestStatuses] = useState<InformationRequestStatus[]>([]);
  const [updateTaskList, setUpdateTaskList] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData>({ requested_documents: [], questions: [] });

  const documentsContext = useContext(DocumentsContext);
  const informationDashboardContext = useContext(InformationDashboard409AContext);
  // This is because the modal can be called from the Reference Widget, Documents page or Information page (Company Dashboard)

  const extractNestedPermissionByKey = useCallback((context: any, keyToSearch: string): unknown | null => {
    if (!context || typeof context !== 'object' || !keyToSearch || typeof keyToSearch !== 'string') {
      return null;
    }

    if (Object.hasOwn(context, 'permissions')) {
      const { permissions } = context;
      if (permissions && typeof permissions === 'object') {
        if (Object.hasOwn(permissions, keyToSearch)) {
          return permissions[keyToSearch];
        }
      }
    }
    return null; 
  }, []);

  const infoRequestLedgerPermission = useMemo(
    () => extractNestedPermissionByKey(documentsContext, 'canUseInfoRequestLedger') ?? extractNestedPermissionByKey(informationDashboardContext, 'canUseInfoRequestLedger'),
    [extractNestedPermissionByKey, documentsContext, informationDashboardContext]
  );
  
  const { handleGetCurrentInformationRequests, currentInformationRequests, hasLoaded, setHasLoaded }
    = useInformation409A({
      currentCompanyMeasurementDateId,
    });

  const createAndSetInformationRequestStatus = useCallback(async () => {
    if (currentCompanyMeasurementDateId === undefined) {
      return;
    }
    const informationRequestStatus = await ApiService.apiCreateInformationRequestStatusCreate({
      report_date: currentCompanyMeasurementDateId,
      valuation_type: is409a ? VALUATION_TYPE_409A : null,
    });
    setInformationRequestStatuses([informationRequestStatus]);
  }, [currentCompanyMeasurementDateId, is409a]);

  const retrieveAndSetInformationRequestStatus = useCallback(async () => {
    if (currentCompanyMeasurementDateId === undefined) {
      return;
    }
    try {
      const informationRequestStatus = await ApiService.apiRetrieveInformationRequestStatusRead(
        currentCompanyMeasurementDateId.toString()
      );

      if (informationRequestStatus.results.length === 0 && infoRequestLedgerPermission) {
        await createAndSetInformationRequestStatus();
        return;
      }

      setInformationRequestStatuses(informationRequestStatus.results);
      setHasLoaded(!isEmpty(informationRequestStatus.results));
    } catch (error) {
      Logger.error('Error retrieving information request status:', error);
    } finally {
      setHasLoaded(true);
    }
  }, [
    createAndSetInformationRequestStatus,
    currentCompanyMeasurementDateId,
    infoRequestLedgerPermission,
    setHasLoaded,
  ]);

  useEffect(() => {
    if (currentCompanyMeasurementDateId) {
      retrieveAndSetInformationRequestStatus();
    }
  }, [currentCompanyMeasurementDateId, retrieveAndSetInformationRequestStatus]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      showLoadingProgressBar();

      if (currentCompany?.id && currentMeasurementDateId) {
        const data = await getCompanyRequestedInformation(currentCompany.id);
        setCompanyData(data);
        await handleGetCurrentInformationRequests();
        setUpdateTaskList(false);
      }

      await retrieveAndSetInformationRequestStatus();

      hideLoadingProgressBar();
    };
    fetchCompanyData();
  }, [
    currentCompany,
    currentMeasurementDateId,
    handleGetCurrentInformationRequests,
    retrieveAndSetInformationRequestStatus,
    updateTaskList,
    reFetch,
  ]);

  // Ensure we don't lose the currentInformationRequests values
  useEffect(() => {
    if (currentInformationRequests && currentInformationRequests.length > 0) {
      setHasLoaded(true);
    }
  }, [currentInformationRequests, setHasLoaded]);

  return {
    informationRequestStatuses,
    companyData,
    setUpdateTaskList,
    handleGetCurrentInformationRequests,
    currentInformationRequests,
    hasLoaded,
  };
};

export default useInformationRequestStatuses;
